/**
 * Máy trạm KHO (#M-16) — tablet treo ở kho, chỉ nghe loa và xem lại mẻ vừa đọc.
 *
 * Tách khỏi máy trạm đơn hàng vì hai bên nghe hai nhóm case khác hẳn nhau: mẻ
 * sổ xưởng là việc của kho, còn đơn hàng, tiền về, giao hàng là việc của bán
 * hàng. Trước đây gộp một màn nên loa kho đọc xen cả tiền về ngân hàng.
 *
 * Chỉ tài khoản có `station.warehouse_view` nhận được event (server lọc người
 * nhận), nên mở màn này bằng tài khoản khác thì bảng sẽ đứng im.
 *
 * Tên file có dấu `_` sau `station` để route nằm ở `/station/warehouse` mà
 * không biến `station.tsx` thành layout (quy ước TanStack Router).
 */

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { FilePlus2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import type { StationNotificationEvent } from '@/hooks/use-station-sse'

import { StationShell, SummaryStat } from '@/components/station/station-shell'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useStationAudio } from '@/hooks/use-station-audio'
import { useStationReadinessHeartbeat } from '@/hooks/use-station-readiness-heartbeat'
import { useStationSSE } from '@/hooks/use-station-sse'
import { m } from '@/lib/paraglide/messages'

/** Số mẻ giữ lại trên bảng. Tablet treo tường đọc từ xa nên dòng phải to; quá
 *  số này là chữ bé lại hoặc phải cuộn, mà không ai đứng cuộn bảng treo tường. */
const MAX_ROWS = 12

/** Bốn sổ có form tạo phiếu (khoá trùng `LedgerKey` của phòng ban). */
type NoteLedger =
    'scenting' | 'packaging_12g5' | 'packaging_other' | 'packaging_box'

function asNoteLedger(v: string | undefined): NoteLedger | undefined {
    return v === 'scenting' ||
        v === 'packaging_12g5' ||
        v === 'packaging_other' ||
        v === 'packaging_box'
        ? v
        : undefined
}

interface LedgerRow {
    id: string
    ledgerName: string
    writerName: string
    at: number
    /** M-134: có đủ hai trường thì dòng có nút "Tạo phiếu". */
    ledger?: NoteLedger
    entryId?: string
}

function clock(ms: number): string {
    const d = new Date(ms)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export const Route = createFileRoute('/_app/_authed/station_/warehouse')({
    component: StationWarehousePage,
})

function StationWarehousePage() {
    return <StationWarehouseView />
}

export function StationWarehouseView() {
    const navigate = useNavigate()
    const exitKiosk = () => navigate({ to: '/', search: { kioskExit: true } })
    const {
        soundEnabled,
        audioBlocked,
        enableSound,
        disableSound,
        playNotificationAlert,
        playTestSound,
    } = useStationAudio()

    const [rows, setRows] = useState<LedgerRow[]>([])
    const { hasPermission } = useAuth()
    // Cùng quyền với nút "Tạo phiếu" bên sổ: sổ lấy hương lập phiếu kho lấy
    // hương, ba sổ đóng gói lập phiếu kho thành phẩm.
    const canWriteScentingNotes = hasPermission?.('scenting_lots.write') ?? true
    const canWriteProductNotes = hasPermission?.('product_lots.write') ?? true

    // Loa đọc xong thì mẻ phải NẰM LẠI trên bảng: người kho đang bận tay, nghe
    // loáng thoáng rồi ngẩng lên mới đọc được là sổ nào, ai ghi.
    const handleLedgerEntry = useCallback(
        (event: StationNotificationEvent) => {
            playNotificationAlert(event)
            const ledgerName = event.payload?.ledger_name
            if (!ledgerName) return
            setRows((prev) =>
                [
                    {
                        id: `${event.occurred_at ?? ''}-${Date.now()}-${Math.random()}`,
                        ledgerName,
                        writerName: event.payload?.writer_name ?? '',
                        at: Date.now(),
                        ledger: asNoteLedger(event.payload?.ledger),
                        entryId: event.payload?.entry_id,
                    },
                    ...prev,
                ].slice(0, MAX_ROWS),
            )
        },
        [playNotificationAlert],
    )

    // Không truyền handler đơn hàng: màn này không đọc và cũng không hiện đơn.
    const { connected: sseConnected } = useStationSSE(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        handleLedgerEntry,
    )
    const readiness = useStationReadinessHeartbeat({
        sseConnected,
        soundEnabled,
        audioBlocked,
        kind: 'warehouse',
    })

    // Wake Lock — giữ màn hình tablet không ngủ.
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null
        const request = async () => {
            try {
                wakeLock = await navigator.wakeLock.request('screen')
            } catch {
                /* not supported or denied */
            }
        }
        void request()
        const onVisible = () => {
            if (document.visibilityState === 'visible') void request()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            void wakeLock?.release()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    return (
        <StationShell
            title={m.station_warehouse_title()}
            connected={sseConnected}
            liveLabel={m.station_warehouse_live()}
            soundEnabled={soundEnabled}
            audioBlocked={audioBlocked}
            onToggleSound={soundEnabled ? disableSound : enableSound}
            onTestSound={playTestSound}
            onExit={exitKiosk}
            readinessNotice={
                readiness.blocked && readiness.blockedAt
                    ? {
                          blockedAt: readiness.blockedAt,
                          requestId: readiness.requestId,
                      }
                    : undefined
            }
            footer={
                <footer className="station-chrome flex items-center justify-between gap-4 border-t border-[var(--line)] px-5 py-2.5 text-xs">
                    <SummaryStat
                        tone="ready"
                        count={rows.length}
                        label={m.station_warehouse_summary()}
                    />
                    <span className="font-semibold uppercase tracking-[0.18em] text-[var(--dim)]">
                        {m.station_warehouse_hint()}
                    </span>
                </footer>
            }
        >
            <main className="min-w-0 flex-1 overflow-auto p-5">
                {rows.length === 0 ? (
                    <p className="pt-16 text-center text-2xl text-[var(--dim)]">
                        {m.station_warehouse_empty()}
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {rows.map((r, i) => (
                            <li
                                key={r.id}
                                className={`flex items-center gap-5 rounded-md px-4 py-3 ${
                                    // Mẻ mới nhất sáng hơn: liếc một cái là biết
                                    // cái loa vừa đọc là dòng nào.
                                    i === 0
                                        ? 'bg-[var(--panel-2)]'
                                        : 'bg-[var(--panel)]'
                                }`}
                            >
                                <span className="w-20 shrink-0 text-2xl font-bold tabular-nums text-[var(--dim)]">
                                    {clock(r.at)}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-3xl font-bold uppercase text-[var(--ink)]">
                                    {r.ledgerName}
                                </span>
                                <span className="shrink-0 text-2xl text-[var(--dim)]">
                                    {r.writerName}
                                </span>
                                {r.ledger &&
                                    r.entryId &&
                                    (r.ledger === 'scenting'
                                        ? canWriteScentingNotes
                                        : canWriteProductNotes) && (
                                        <CreateNoteButton
                                            ledger={r.ledger}
                                            entryId={r.entryId}
                                        />
                                    )}
                            </li>
                        ))}
                    </ul>
                )}
            </main>
        </StationShell>
    )
}

/** M-134: mở form tạo phiếu của đúng mẻ ở TAB MỚI — giống nút "Tạo phiếu" bên
 *  sổ, nhưng màn máy trạm vẫn mở để loa tiếp tục đọc. Mẻ đã có phiếu thì form
 *  báo lỗi lúc lưu, không tạo trùng. */
function CreateNoteButton({
    ledger,
    entryId,
}: {
    ledger: NoteLedger
    entryId: string
}) {
    const label = (
        <>
            <FilePlus2 className="mr-2 size-5" />
            {m.station_warehouse_create_note()}
        </>
    )
    const linkProps = { target: '_blank', rel: 'noopener noreferrer' } as const
    return (
        <Button asChild size="lg" className="h-12 shrink-0 text-lg">
            {ledger === 'scenting' ? (
                <Link
                    to="/scenting-warehouse/stock-in-notes/new"
                    search={{ logEntryId: entryId }}
                    {...linkProps}
                >
                    {label}
                </Link>
            ) : ledger === 'packaging_12g5' ? (
                <Link
                    to="/sales-warehouse/stock-in-notes/new"
                    search={{ packagingLogEntryId: entryId }}
                    {...linkProps}
                >
                    {label}
                </Link>
            ) : ledger === 'packaging_other' ? (
                <Link
                    to="/sales-warehouse/stock-in-notes/new"
                    search={{ otherLogEntryId: entryId }}
                    {...linkProps}
                >
                    {label}
                </Link>
            ) : (
                <Link
                    to="/sales-warehouse/stock-in-notes/new"
                    search={{ boxLogEntryId: entryId }}
                    {...linkProps}
                >
                    {label}
                </Link>
            )}
        </Button>
    )
}
